import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import Swal from "sweetalert2";
import axios from "axios";
import "./AddUser.css";
import { API_BASE } from "../../../ApiUrl";
import PeopleTabs from "./PeopleTabs";

const API_URL = `${API_BASE}/users/`;
const TENANT_RECORDS_API_URL = `${API_BASE}/tenant-records/`;

const emptyUser = {
  id: null,
  email: "",
  mobile_country_code: "+91",
  mobile_number: "",
  password: ""
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AddUser = () => {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState([{ ...emptyUser }]);
  const [tenantId, setTenantId] = useState(null);

  const getTenantUser = () => {
    try {
      const tenantUser = localStorage.getItem("tenant_user");
      return tenantUser ? JSON.parse(tenantUser) : null;
    } catch (error) {
      console.error("Error reading tenant_user:", error);
      return null;
    }
  };

  // ---- Get tenant id ----
  useEffect(() => {
    const tenantUser = getTenantUser();
    if (tenantUser?.tenant_id) setTenantId(Number(tenantUser.tenant_id));
  }, []);

  // ---- Fetch users ----
  useEffect(() => {
    if (tenantId) fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const fetchUsers = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        TENANT_RECORDS_API_URL,
        {
          tenant_id: tenantId,
          tables: ["users"]
        },
        { headers: { "Content-Type": "application/json" } }
      );

      const result = response.data;

      let rows = [];
      if (result?.data?.users) {
        rows = result.data.users;
      } else if (result?.users) {
        rows = result.users;
      } else if (Array.isArray(result?.data)) {
        rows = result.data;
      } else if (Array.isArray(result)) {
        rows = result;
      }

      setUsers(rows);
    } catch (error) {
      console.error("Error fetching users:", error);
      Swal.fire({
        icon: "error",
        title: "Unable to load users",
        text:
          error.response?.data?.detail ||
          "Something went wrong while loading users."
      });
    } finally {
      setLoading(false);
    }
  };

  // ---- Form handlers ----
  const handleChange = (index, e) => {
    const { name, value } = e.target;
    setFormData((prev) =>
      prev.map((u, i) => (i === index ? { ...u, [name]: value } : u))
    );
  };

  const handleAdd = () => {
    setFormData([{ ...emptyUser }]);
    setIsEditing(false);
    setEditingId(null);
    setShowForm(true);
  };

  const handleAddAnother = () => {
    setFormData((prev) => [...prev, { ...emptyUser }]);
  };

  const handleRemoveForm = (index) => {
    setFormData((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEdit = (u) => {
    setFormData([
      {
        id: u.id,
        email: u.email || "",
        mobile_country_code: u.mobile_country_code || "+91",
        mobile_number: u.mobile_number || "",
        password: ""
      }
    ]);
    setEditingId(u.id);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDelete = async (u) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Delete User?",
      text: `Are you sure you want to delete "${u.email || "this user"}"?`,
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626"
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${API_URL}${u.id}/`);

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "User deleted successfully.",
        timer: 1500,
        showConfirmButton: false
      });

      fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text:
          error.response?.data?.detail ||
          "Unable to delete the user."
      });
    }
  };

  // ---- Validation ----
  const validateRow = (u, index) => {
    const label = `User ${index + 1}`;

    const isEmptyRow =
      !u.email.trim() && !u.mobile_number.trim() && !u.id;
    if (isEmptyRow) return true;

    if (!u.email.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Required Field Missing",
        text: `Please provide an Email for ${label}.`
      });
      return false;
    }

    if (!EMAIL_REGEX.test(u.email.trim())) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Email",
        text: `Email for ${label} is not a valid email address.`
      });
      return false;
    }

    if (!u.mobile_country_code.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Country Code Missing",
        text: `Please provide a Mobile Country Code for ${label}.`
      });
      return false;
    }

    if (!/^\+\d{1,4}$/.test(u.mobile_country_code.trim())) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Country Code",
        text: `Mobile Country Code for ${label} must look like +91 or +1.`
      });
      return false;
    }

    if (!u.mobile_number.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Required Field Missing",
        text: `Please provide a Mobile Number for ${label}.`
      });
      return false;
    }

    if (!/^\d{4,15}$/.test(u.mobile_number.trim())) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Mobile Number",
        text: `Mobile Number for ${label} must be 4–15 digits.`
      });
      return false;
    }

    return true;
  };

  const extractError = (error) => {
    if (error.response?.data) {
      const d = error.response.data;
      if (typeof d === "string") return d;
      if (d.detail) return d.detail;
      if (d.message) return d.message;
      if (d.errors) {
        return Object.entries(d.errors)
          .map(
            ([field, errors]) =>
              `${field}: ${Array.isArray(errors) ? errors.join(", ") : errors}`
          )
          .join(" | ");
      }
      return JSON.stringify(d);
    }
    return error.message || "Unknown error";
  };

  const buildPayload = (u) => ({
    email: u.email.trim().toLowerCase(),
    mobile_country_code: u.mobile_country_code.trim(),
    mobile_number: u.mobile_number.trim(),
    tenant: tenantId
  });

  // ---- Submit ----
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tenantId) {
      Swal.fire({
        icon: "error",
        title: "Tenant ID missing",
        text: "Unable to identify the current tenant."
      });
      return;
    }

    for (let i = 0; i < formData.length; i++) {
      if (!validateRow(formData[i], i)) return;
    }

    const activeRows = formData.filter(
      (u) => u.email.trim() || u.mobile_number.trim() || u.id
    );

    // Duplicate email check within the form
    const emails = activeRows.map((u) => u.email.trim().toLowerCase());
    const dupEmailIdx = emails.findIndex((x, i) => emails.indexOf(x) !== i);
    if (dupEmailIdx !== -1) {
      Swal.fire({
        icon: "warning",
        title: "Duplicate Email",
        text: `Email "${activeRows[dupEmailIdx].email.trim()}" is used more than once.`
      });
      return;
    }

    // Duplicate mobile check within the form
    const mobiles = activeRows.map(
      (u) => `${u.mobile_country_code.trim()}${u.mobile_number.trim()}`
    );
    const dupMobIdx = mobiles.findIndex((x, i) => mobiles.indexOf(x) !== i);
    if (dupMobIdx !== -1) {
      Swal.fire({
        icon: "warning",
        title: "Duplicate Mobile",
        text: `Mobile "${mobiles[dupMobIdx]}" is used more than once.`
      });
      return;
    }

    const CONFIG = {
      headers: { "Content-Type": "application/json" }
    };

    try {
      setLoading(true);

      if (isEditing) {
        const payload = buildPayload(formData[0]);
        await axios.patch(`${API_URL}${editingId}/`, payload, CONFIG);

        await Swal.fire({
          icon: "success",
          title: "Updated Successfully",
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        for (const u of activeRows) {
          await axios.post(API_URL, buildPayload(u), CONFIG);
        }

        await Swal.fire({
          icon: "success",
          title: "Created Successfully",
          text: `${activeRows.length} user${
            activeRows.length > 1 ? "s" : ""
          } created successfully.`,
          timer: 1500,
          showConfirmButton: false
        });
      }

      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      setFormData([{ ...emptyUser }]);

      await fetchUsers();
    } catch (error) {
      console.error("Error saving user:", error);

      Swal.fire({
        icon: "error",
        title: isEditing ? "Update failed" : "Creation failed",
        text: extractError(error)
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setIsEditing(false);
    setEditingId(null);
    setFormData([{ ...emptyUser }]);
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="user-page">
          <PeopleTabs />

          {!showForm ? (
            <>
              {/* ==================================================
                  LIST HEADER
              ================================================== */}

              <div className="user-page-header">
                <div>
                  <h2>Tenant Users</h2>
                </div>

                <button
                  type="button"
                  className="user-add-top-btn"
                  onClick={handleAdd}
                >
                  + Add User
                </button>
              </div>

              {/* ==================================================
                  TABLE
              ================================================== */}

              <div className="user-list-card">
                {loading ? (
                  <div className="user-loading">
                    Loading users...
                  </div>
                ) : users.length === 0 ? (
                  <div className="user-empty">
                    <div className="user-empty-icon">+</div>
                    <h3>No Users Found</h3>
                    <p>
                      There are no users available for the current tenant.
                    </p>
                    <button
                      type="button"
                      className="user-empty-add-btn"
                      onClick={handleAdd}
                    >
                      + Add User
                    </button>
                  </div>
                ) : (
                  <div className="user-table-wrapper">
                    <table className="user-table">
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Country Code</th>
                          <th>Mobile Number</th>
                          <th>Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {users.map((u) => (
                          <tr key={u.id}>
                            <td>
                              <strong>{u.email || "-"}</strong>
                            </td>

                            <td className="user-muted">
                              {u.mobile_country_code || "-"}
                            </td>

                            <td className="user-muted">
                              {u.mobile_number || "-"}
                            </td>

                            <td>
                              <div className="user-actions">
                                <button
                                  type="button"
                                  className="user-edit-btn"
                                  onClick={() => handleEdit(u)}
                                >
                                  Edit
                                </button>

                                <button
                                  type="button"
                                  className="user-delete-btn"
                                  onClick={() => handleDelete(u)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* ==================================================
                  ADD / EDIT HEADER
              ================================================== */}

              <div className="user-page-header">
                <div>
                  <h2>{isEditing ? "Edit User" : "Create Users"}</h2>
                </div>

                <button
                  type="button"
                  className="user-back-btn"
                  onClick={handleCancel}
                >
                  ← Back
                </button>
              </div>

              {/* ==================================================
                  FORM
              ================================================== */}

              <form onSubmit={handleSubmit}>
                {formData.map((u, index) => (
                  <div
                    className="user-card"
                    key={index}
                    style={{ marginBottom: "20px" }}
                  >
                    <div className="user-form-body">
                      <div className="user-record-header">
                        <h4>User {index + 1}</h4>

                        {!isEditing && formData.length > 1 && (
                          <button
                            type="button"
                            className="user-remove-btn"
                            onClick={() => handleRemoveForm(index)}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div className="user-form-grid">
                        <div className="user-form-group user-form-group--full">
                          <label>
                            Email <span>*</span>
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={u.email}
                            onChange={(e) => handleChange(index, e)}
                            placeholder="e.g. jane.doe@example.com"
                            maxLength={254}
                            autoComplete="off"
                            required
                          />
                        </div>

                        <div className="user-form-group">
                          <label>
                            Mobile Country Code <span>*</span>
                          </label>
                          <input
                            type="text"
                            name="mobile_country_code"
                            value={u.mobile_country_code}
                            onChange={(e) => handleChange(index, e)}
                            placeholder="+91"
                            maxLength={5}
                            required
                          />
                        </div>

                        <div className="user-form-group">
                          <label>
                            Mobile Number <span>*</span>
                          </label>
                          <input
                            type="tel"
                            name="mobile_number"
                            value={u.mobile_number}
                            onChange={(e) => handleChange(index, e)}
                            placeholder="e.g. 9876543210"
                            maxLength={15}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {!isEditing && (
                  <button
                    type="button"
                    className="user-add-btn"
                    onClick={handleAddAnother}
                    style={{ marginBottom: "20px" }}
                  >
                    <span>+</span>
                    Add Another User
                  </button>
                )}

                <div className="user-form-actions">
                  <button
                    type="button"
                    className="user-cancel-btn"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="user-save-btn"
                    disabled={loading}
                  >
                    {loading
                      ? "Saving..."
                      : isEditing
                      ? "Update User"
                      : "Save Users"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddUser;